import React, { useCallback, useEffect, useState } from "react";
import Button from "designer/ui/smart/button/button";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { isValidEmailAddress } from "common/Support";
import { trackEvent } from "analytics";
import { TeamInviteStyle } from "./team-invite-styles";
import { addOrgMember, OrgMember } from "designer/apps/orgs/orgs-lib";
import { OrgIcon } from "../../apps/orgs/orgs-sidebar/org-icon";
import { icons } from "../../apps/orgs/orgs-sidebar/org-subtitle-sidebar-item";
import AppIcon from "common/app-icon";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Organization } from "common/Database";
import classNames from "classnames";

interface TeamMemberRowProps {
    readonly email: string;
    readonly photoURL?: string;
    readonly admin: boolean;
    readonly status: "pending" | "active";
    readonly isUser?: boolean;
    readonly sendInvites: (email: string[]) => Promise<void>;
}

const TeamMemberRow: React.FunctionComponent<TeamMemberRowProps> = ({
    email,
    photoURL,
    status,
    admin,
    sendInvites,
    isUser = false,
}) => (
    <li className={classNames("invite", status === "pending" && "pending")}>
        <div className="invite-email-container">
            {photoURL === undefined ? (
                <div className="mems-user-photo" />
            ) : (
                <img className="mems-user-photo" src={photoURL} alt="" />
            )}
            <div className="invite-email">
                {email}
                {isUser && ` (You)`}
            </div>
        </div>
        <div className="invite-actions">
            {status === "pending" && (
                <div className="invite-sent">
                    Invited{" "}
                    <button
                        onClick={async () => {
                            await sendInvites([email]);
                        }}>
                        Resend
                    </button>
                </div>
            )}
            {admin && <div className="admin">{isUser ? `Owner` : `Admin`}</div>}
        </div>
    </li>
);

interface TeamInviteProps extends RouteComponentProps {
    readonly organization: Organization;
    readonly userID: string;
    readonly maxCount?: number;
    readonly orgMembers: readonly OrgMember[];
    readonly source: string;
}

const TeamInviteImpl: React.FunctionComponent<TeamInviteProps> = p => {
    const { organization, history, userID, orgMembers, maxCount = 8, source } = p;
    const { id: orgID, displayName: orgDisplayName } = organization;

    const [currentEmails, setCurrentEmails] = useState("");
    const [emailValid, setEmailValid] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [inviteSucceeded, setInviteSucceeded] = useState(true);
    const [emails, setEmails] = useState<string[]>([]);
    const [folded, setFolded] = useState(false);
    const [warning, setWarning] = useState<string | undefined>(undefined);

    const onInputChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.target.value;
        setCurrentEmails(value);
        setWarning(undefined);

        const cleanedEmails = value.split(",").map(v => v.trim());
        const isValidEmail = cleanedEmails.every(isValidEmailAddress);

        setEmailValid(isValidEmail);
    }, []);

    const sendInvites = useCallback(
        async (emailsToSend: string[]) => {
            setInviting(true);
            const resp = addOrgMember(orgID, emailsToSend);
            trackEvent("org_member_invited", { org_id: orgID, source: source });
            setInviting(false);

            if (resp !== undefined) {
                setInviteSucceeded(true);
            } else {
                setInviteSucceeded(false);
                return;
            }
        },
        [orgID, source]
    );

    useEffect(() => {
        // Set warnings
        if (!inviteSucceeded) {
            setWarning("Unable to invite user. Please try again");
        }
    }, [inviteSucceeded]);

    const validateAndSendInvites = useCallback(async () => {
        const cleanedEmails = currentEmails.split(",").map(v => v.trim());
        const isValidEmail = cleanedEmails.every(isValidEmailAddress);

        if (!isValidEmail) {
            return;
        }

        const existingEmails: Set<string> = new Set([...orgMembers.map(v => v.email).concat(emails)]);
        const duplicateEmails = cleanedEmails.filter(e => existingEmails.has(e));
        duplicateEmails.forEach(e => setWarning(`Email already invited: ${e}`));
        if (duplicateEmails.length > 0) return;

        await sendInvites(cleanedEmails);
        setEmails([...emails, ...cleanedEmails]);
    }, [emails, currentEmails, orgMembers, sendInvites]);

    const onKeyPress = useCallback(
        async (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                await validateAndSendInvites();
            }
        },
        [validateAndSendInvites]
    );

    const onFold = useCallback(() => setFolded(!folded), [folded]);

    const membersIcon = icons["members"];
    const onManageClick = useCallback(() => {
        history.push(`/o/${orgID}/members`);
    }, [orgID, history]);

    const inviteText = inviting ? "Inviting..." : "Invite";

    const rowsHidden: number = emails.length + orgMembers.length - maxCount;
    const showMore: boolean = rowsHidden > 0;

    return (
        <TeamInviteStyle>
            <div className="title">Invite people to your team</div>
            <div className="email-container">
                <div className="send-invite">
                    <div className="email-input">
                        <InputLabel
                            warning={warning}
                            label={undefined}
                            placeholder="Enter emails (comma seperated)"
                            onChange={onInputChange}
                            onKeyPress={onKeyPress}
                        />
                    </div>
                    <div className="email-send-button">
                        <Button
                            disabled={inviting || !emailValid}
                            variant="filled"
                            inline={true}
                            type="button"
                            onClick={validateAndSendInvites}
                            width={82}>
                            {inviteText}
                        </Button>
                    </div>
                </div>
                <div className="team-folder">
                    <div className="org-icon">
                        <OrgIcon size={"large"} label={orgDisplayName} />
                    </div>
                    <div className="org-name">{orgDisplayName}</div>
                    <AppIcon
                        icon="caret"
                        size={10}
                        className="caret pointer"
                        degreesOfRotation={folded ? -90 : 0}
                        onClick={onFold}
                    />
                </div>
                {!folded && (
                    <ul className="invites">
                        {emails.map(email => (
                            <TeamMemberRow
                                key={email}
                                email={email}
                                admin={false}
                                status={"pending"}
                                sendInvites={sendInvites}
                            />
                        ))}
                        {orgMembers.slice(0, Math.max(0, maxCount - emails.length)).map(m => (
                            <TeamMemberRow
                                key={m.email}
                                email={m.email}
                                admin={m.role === "admin"}
                                isUser={m.userID === userID}
                                status={m.status}
                                photoURL={m.photoURL}
                                sendInvites={sendInvites}
                            />
                        ))}
                        {showMore && (
                            <li key={"showMore"} className="invite">
                                <div className="invite-email-container">
                                    <div className="mems-user-photo" />
                                    <div className="invite-email">and {rowsHidden} more...</div>
                                </div>
                            </li>
                        )}
                    </ul>
                )}
                {orgMembers.length === 1 && (
                    <div className="notice">
                        <AppIcon icon={"infoCircle"} />
                        <span>
                            Team Members can create, edit, and upgrade apps. They can also access app data and invite
                            other members.{" "}
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://docs.glideapps.com/all/reference/security-and-per-user-data/controlling-who-can-access-your-app#private-apps">
                                Learn more
                            </a>
                        </span>
                    </div>
                )}
                <div className="manage-team">
                    <button onClick={onManageClick}>
                        {membersIcon}
                        Manage team members
                    </button>
                </div>
            </div>
        </TeamInviteStyle>
    );
};

const TeamInvite = withRouter(TeamInviteImpl);
export default TeamInvite;
